import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus, Edit, Trash2, Search, Building2, Globe, Shield, Phone, Mail, 
  Calendar, AlertTriangle, CheckCircle2, Clock, XCircle, FileUp, 
  ExternalLink, ChevronDown, ChevronUp, Filter, Download, RefreshCw,
  Loader2
} from 'lucide-react';
import { Supplier } from '../../types/grc';
import { useI18n } from '../../context/I18nContext';
import { authService } from '../../services/authService';

interface SupplierManagementProps {
  userRole: 'admin' | 'editor' | 'viewer';
}

// Local storage key for suppliers (until backend is ready)
const STORAGE_KEY = 'grc_suppliers';

const loadSuppliers = (): Supplier[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};

const saveSuppliers = (suppliers: Supplier[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(suppliers));
};

const CATEGORIES: { value: Supplier['category']; label: string }[] = [
  { value: 'cloud_provider', label: 'Cloud Provider' },
  { value: 'saas', label: 'SaaS' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'outsourcing', label: 'Outsourcing' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'software', label: 'Software' },
  { value: 'telecom', label: 'Telecom' },
  { value: 'other', label: 'Other' },
];

const RISK_TIERS: { value: Supplier['risk_tier']; label: string; color: string }[] = [
  { value: 'critical', label: 'Critical', color: 'text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-900/30 dark:border-red-800' },
  { value: 'high', label: 'High', color: 'text-orange-700 bg-orange-50 border-orange-200 dark:text-orange-300 dark:bg-orange-900/30 dark:border-orange-800' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-700 bg-yellow-50 border-yellow-200 dark:text-yellow-300 dark:bg-yellow-900/30 dark:border-yellow-800' },
  { value: 'low', label: 'Low', color: 'text-green-700 bg-green-50 border-green-200 dark:text-green-300 dark:bg-green-900/30 dark:border-green-800' },
];

const DATA_ACCESS_LEVELS: { value: Supplier['data_access_level']; label: string }[] = [
  { value: 'none', label: 'No Access' },
  { value: 'public', label: 'Public Data' },
  { value: 'internal', label: 'Internal Data' },
  { value: 'confidential', label: 'Confidential' },
  { value: 'restricted', label: 'Restricted / PII' },
];

const getComplianceBadge = (status: Supplier['compliance_status']) => {
  switch (status) {
    case 'compliant': return { icon: CheckCircle2, label: 'Compliant', cls: 'text-green-700 bg-green-50 border-green-200 dark:text-green-300 dark:bg-green-900/30' };
    case 'partial': return { icon: Clock, label: 'Partial', cls: 'text-yellow-700 bg-yellow-50 border-yellow-200 dark:text-yellow-300 dark:bg-yellow-900/30' };
    case 'non_compliant': return { icon: XCircle, label: 'Non-Compliant', cls: 'text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-900/30' };
    default: return { icon: AlertTriangle, label: 'Not Assessed', cls: 'text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-400 dark:bg-gray-700' };
  }
};

const getStatusBadge = (status: Supplier['status']) => {
  switch (status) {
    case 'active': return 'text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-900/30';
    case 'inactive': return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700';
    case 'under_review': return 'text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/30';
    case 'terminated': return 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30';
    default: return 'text-gray-600 bg-gray-100';
  }
};

export const SupplierManagement: React.FC<SupplierManagementProps> = ({ userRole }) => {
  const { t } = useI18n();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Check permissions (section-based RBAC)
  const canEdit = authService.hasPermission('suppliers', 'edit') || authService.isGlobalAdmin() || authService.isOrgAdmin();

  useEffect(() => {
    setSuppliers(loadSuppliers());
    setLoading(false);
  }, []);

  const save = (updated: Supplier[]) => {
    setSuppliers(updated);
    saveSuppliers(updated);
  };

  const handleDelete = (id: number) => {
    if (!confirm('Delete this supplier?')) return;
    save(suppliers.filter(s => s.id !== id));
  };

  const filteredSuppliers = useMemo(() => {
    let result = suppliers;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s => 
        s.name.toLowerCase().includes(q) || 
        (s.description || '').toLowerCase().includes(q) ||
        (s.services_provided || '').toLowerCase().includes(q) ||
        (s.contact_name || '').toLowerCase().includes(q)
      );
    }
    if (categoryFilter !== 'all') result = result.filter(s => s.category === categoryFilter);
    if (riskFilter !== 'all') result = result.filter(s => s.risk_tier === riskFilter);
    if (statusFilter !== 'all') result = result.filter(s => s.status === statusFilter);
    return result;
  }, [suppliers, searchQuery, categoryFilter, riskFilter, statusFilter]);

  const stats = useMemo(() => ({
    total: suppliers.length,
    active: suppliers.filter(s => s.status === 'active').length,
    critical: suppliers.filter(s => s.risk_tier === 'critical').length,
    nonCompliant: suppliers.filter(s => s.compliance_status === 'non_compliant').length,
    expiringSoon: suppliers.filter(s => {
      if (!s.contract_end) return false;
      const d = new Date(s.contract_end);
      const now = new Date();
      const diff = (d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
      return diff > 0 && diff < 90;
    }).length,
  }), [suppliers]);

  if (loading) {
    return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label={t.suppliersExt.totalSuppliers} value={stats.total} icon={Building2} color="text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30" />
        <StatCard label={t.suppliersExt.active} value={stats.active} icon={CheckCircle2} color="text-green-600 bg-green-50 dark:bg-green-900/30" />
        <StatCard label={t.suppliersExt.criticalTier} value={stats.critical} icon={AlertTriangle} color="text-red-600 bg-red-50 dark:bg-red-900/30" />
        <StatCard label={t.suppliersExt.nonCompliant} value={stats.nonCompliant} icon={XCircle} color="text-orange-600 bg-orange-50 dark:bg-orange-900/30" />
        <StatCard label={t.suppliersExt.expiringContract} value={stats.expiringSoon} icon={Calendar} color="text-amber-600 bg-amber-50 dark:bg-amber-900/30" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
          placeholder={t.suppliersExt.searchSuppliers}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        />
        </div>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white">
          <option value="all">{t.suppliersExt.allCategories}</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select value={riskFilter} onChange={e => setRiskFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white">
          <option value="all">{t.suppliersExt.allRiskTiers}</option>
          {RISK_TIERS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white">
          <option value="all">{t.suppliersExt.allStatuses}</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="under_review">Under Review</option>
          <option value="terminated">Terminated</option>
        </select>
        {canEdit && (
          <button onClick={() => { setEditingSupplier(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium">
            <Plus className="w-4 h-4" /> {t.suppliersExt.addSupplier}
          </button>
        )}
      </div>

      {/* Supplier List */}
      {filteredSuppliers.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">{suppliers.length === 0 ? t.suppliersExt.noSuppliers : t.common.noResults}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredSuppliers.map(supplier => {
            const compliance = getComplianceBadge(supplier.compliance_status);
            const CompIcon = compliance.icon;
            const riskTier = RISK_TIERS.find(r => r.value === supplier.risk_tier);
            const isExpanded = expandedId === supplier.id;

            return (
              <div
                key={supplier.id}
                className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 transition-all hover:shadow-lg ${canEdit ? 'cursor-pointer' : ''}`}
              >
                <div className="p-4" onClick={() => canEdit ? (setEditingSupplier(supplier), setShowModal(true)) : setExpandedId(isExpanded ? null : supplier.id)}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <Building2 className="w-5 h-5 text-indigo-500" />
                        <h3 className="font-semibold text-gray-900 dark:text-white">{supplier.name}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(supplier.status)}`}>
                          {supplier.status.replace('_', ' ')}
                        </span>
                      </div>
                      {supplier.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 ml-8">{supplier.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {riskTier && (
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${riskTier.color}`}>
                          {riskTier.label} Risk
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded text-xs font-medium border flex items-center gap-1 ${compliance.cls}`}>
                        <CompIcon className="w-3 h-3" /> {compliance.label}
                      </span>
                    </div>
                  </div>

                  {/* Always-visible details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-xs">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Category:</span>
                      <span className="ml-1 font-medium text-gray-700 dark:text-gray-300 capitalize">{supplier.category.replace('_', ' ')}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Data Access:</span>
                      <span className="ml-1 font-medium text-gray-700 dark:text-gray-300 capitalize">{supplier.data_access_level?.replace('_', ' ') || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Contact:</span>
                      <span className="ml-1 font-medium text-gray-700 dark:text-gray-300">{supplier.contact_name || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Contract Ends:</span>
                      <span className="ml-1 font-medium text-gray-700 dark:text-gray-300">{supplier.contract_end || 'N/A'}</span>
                    </div>
                  </div>

                  {/* Footer */}
                  {canEdit && (
                    <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-100 dark:border-gray-600">
                      <span className="text-xs text-gray-400 flex items-center gap-1"><Edit className="w-3 h-3" /> Click to edit</span>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(supplier.id); }}
                        className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20">
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {t.suppliersExt.showing} {filteredSuppliers.length} {t.common.of} {suppliers.length} {t.suppliers.title.toLowerCase()}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">GRC Pro</span>
          <span className="text-xs text-gray-300">•</span>
          <span className="text-xs text-gray-400">{t.suppliers.management}</span>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <SupplierModal
          supplier={editingSupplier}
          onClose={() => { setShowModal(false); setEditingSupplier(null); }}
          onSave={(data) => {
            if (editingSupplier) {
              save(suppliers.map(s => s.id === editingSupplier.id ? { ...editingSupplier, ...data } : s));
            } else {
              const newId = suppliers.length > 0 ? Math.max(...suppliers.map(s => s.id)) + 1 : 1;
              save([...suppliers, { ...data, id: newId } as Supplier]);
            }
            setShowModal(false);
            setEditingSupplier(null);
          }}
        />
      )}
    </div>
  );
};

// ─── Stat Card ─────────────────
const StatCard: React.FC<{ label: string; value: number; icon: React.ComponentType<any>; color: string }> = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      </div>
    </div>
  </div>
);

// ─── Supplier Modal ────────────
interface SupplierModalProps {
  supplier: Supplier | null;
  onClose: () => void;
  onSave: (data: Partial<Supplier>) => void;
}

const SupplierModal: React.FC<SupplierModalProps> = ({ supplier, onClose, onSave }) => {
  const [form, setForm] = useState<Partial<Supplier>>({
    name: supplier?.name || '',
    description: supplier?.description || '',
    contact_name: supplier?.contact_name || '',
    contact_email: supplier?.contact_email || '',
    contact_phone: supplier?.contact_phone || '',
    website: supplier?.website || '',
    category: supplier?.category || 'other',
    risk_tier: supplier?.risk_tier || 'medium',
    status: supplier?.status || 'active',
    data_access_level: supplier?.data_access_level || 'none',
    services_provided: supplier?.services_provided || '',
    compliance_status: supplier?.compliance_status || 'not_assessed',
    contract_start: supplier?.contract_start || '',
    contract_end: supplier?.contract_end || '',
    sla_details: supplier?.sla_details || '',
    certifications: supplier?.certifications || [],
    notes: supplier?.notes || '',
    linked_framework_ids: supplier?.linked_framework_ids || [],
  });
  const [certInput, setCertInput] = useState('');

  const addCert = () => {
    if (certInput.trim()) {
      setForm({ ...form, certifications: [...(form.certifications || []), certInput.trim()] });
      setCertInput('');
    }
  };

  const removeCert = (idx: number) => {
    setForm({ ...form, certifications: (form.certifications || []).filter((_, i) => i !== idx) });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{supplier ? 'Edit Supplier' : 'Add New Supplier'}</h2>
        </div>
        <div className="p-6 space-y-4">
          {/* Name & Description */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Supplier Name *</label>
              <input type="text" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Website</label>
              <input type="url" value={form.website || ''} onChange={e => setForm({ ...form, website: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" placeholder="https://" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" rows={2} />
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Name</label>
              <input type="text" value={form.contact_name || ''} onChange={e => setForm({ ...form, contact_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input type="email" value={form.contact_email || ''} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
              <input type="tel" value={form.contact_phone || ''} onChange={e => setForm({ ...form, contact_phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" />
            </div>
          </div>

          {/* Classification */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
              <select value={form.category || 'other'} onChange={e => setForm({ ...form, category: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Risk Tier</label>
              <select value={form.risk_tier || 'medium'} onChange={e => setForm({ ...form, risk_tier: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white">
                {RISK_TIERS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select value={form.status || 'active'} onChange={e => setForm({ ...form, status: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="under_review">Under Review</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Data Access</label>
              <select value={form.data_access_level || 'none'} onChange={e => setForm({ ...form, data_access_level: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white">
                {DATA_ACCESS_LEVELS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
          </div>

          {/* Compliance & Contract */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Compliance Status</label>
              <select value={form.compliance_status || 'not_assessed'} onChange={e => setForm({ ...form, compliance_status: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white">
                <option value="not_assessed">Not Assessed</option>
                <option value="compliant">Compliant</option>
                <option value="partial">Partial</option>
                <option value="non_compliant">Non-Compliant</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Contract Start</label>
              <input type="date" value={form.contract_start || ''} onChange={e => setForm({ ...form, contract_start: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Contract End</label>
              <input type="date" value={form.contract_end || ''} onChange={e => setForm({ ...form, contract_end: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" />
            </div>
          </div>

          {/* Services & SLA */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Services Provided</label>
            <textarea value={form.services_provided || ''} onChange={e => setForm({ ...form, services_provided: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" rows={2} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">SLA Details</label>
            <textarea value={form.sla_details || ''} onChange={e => setForm({ ...form, sla_details: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" rows={2} />
          </div>

          {/* Certifications */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Certifications</label>
            <div className="flex gap-2 mb-2">
              <input type="text" value={certInput} onChange={e => setCertInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCert())}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                placeholder="e.g., ISO 27001, SOC 2 Type II" />
              <button onClick={addCert} className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(form.certifications || []).map((cert, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                  <Shield className="w-3 h-3" /> {cert}
                  <button onClick={() => removeCert(i)} className="ml-1 text-blue-500 hover:text-blue-700">&times;</button>
                </span>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
            <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" rows={2} />
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">Cancel</button>
          <button onClick={() => form.name && onSave(form)} 
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            disabled={!form.name}>
            {supplier ? 'Update' : 'Create'} Supplier
          </button>
        </div>
      </div>
    </div>
  );
};
